import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { IMAGES } from "@/data/site";
import { Eyebrow } from "./Reveal";
import { HouseDrawing, BlueprintGrid, CropMarks } from "./Blueprint";
import { EASE } from "./motion";

const MLink = motion.create(Link);
const LINES = [["Make your"], ["dream", " into"], ["reality."]];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const drawingOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <BlueprintGrid />
      <motion.div style={{ opacity: drawingOpacity }} className="pointer-events-none absolute -right-10 top-6 w-[520px] max-w-[85vw] text-stone-400/60 lg:left-[46%] lg:right-auto lg:top-10 lg:w-[560px]" aria-hidden="true">
        <HouseDrawing duration={2.6} delay={0.6} />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <Eyebrow className="mb-8">Carvings Group — Architecture & Construction, Kannur</Eyebrow>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-stone-900" data-testid="hero-title">
            {LINES.map((parts, i) => (
              <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                <motion.span
                  className="block"
                  initial={{ y: "110%", rotate: 2 }}
                  animate={{ y: 0, rotate: 0 }}
                  transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {parts.map((p, j) => (p === "dream" ? <em key={j} className="text-[#B85C38]">{p}</em> : p))}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9, ease: EASE }} className="mt-10 max-w-md text-stone-600 text-base leading-relaxed font-light">
            Carvings Architecture, led by Gokul K.V., designs and builds homes across Kerala — from the first sketch and 3D view to the last brick on site.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.05, ease: EASE }} className="mt-10 flex flex-wrap gap-4">
            <MLink whileTap={{ scale: 0.96 }} to="/projects" className="btn-dark" data-testid="hero-view-projects-btn">View projects <ArrowDownRight size={16} /></MLink>
            <MLink whileTap={{ scale: 0.96 }} to="/contact" className="btn-ghost" data-testid="hero-start-project-btn">Start a project</MLink>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5">
          <motion.div
            className="relative aspect-[4/5] overflow-hidden bg-stone-200"
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
          >
            <motion.img style={{ y: imgY }} initial={{ scale: 1.3 }} animate={{ scale: 1.12 }} transition={{ duration: 2.4, delay: 0.5, ease: EASE }} src={IMAGES.hero} alt="Ajeer Residence, Payyanur — 3D view by Carvings Architecture" className="w-full h-full object-cover" data-testid="hero-image" />
          </motion.div>
          <div className="relative mt-3 flex items-center justify-between">
            <CropMarks delay={1.6} />
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="font-mono text-[11px] tracking-wider text-stone-500 py-1 pl-7">AJEER RESIDENCE — PAYYANUR</motion.p>
            <motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.7, duration: 0.8, ease: EASE }} className="hidden sm:block h-px flex-1 mx-6 bg-stone-300 origin-left" />
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="font-mono text-[11px] tracking-wider text-stone-500 pr-7">SCALE 1:100</motion.p>
          </div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }} className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center text-stone-400" aria-hidden="true">
        <span className="plumb" />
        <span className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
      </motion.div>
    </section>
  );
};
